import { Button } from '@common/components';
import { useTranslations } from '@lang';
import { AddIcon } from '@root/styles/icons';
import { femaleTitlesOptions, maleTitlesOptions } from '@utils/guestTitles';
import { FC, useEffect } from 'react';
import idGenerator from 'react-id-generator';
import { Field } from 'redux-form';
import { genderOptions, genderOptionsWithNeutral } from '../../../constants';
import { ReduxFormInput } from '../../FormElements/BlockInputField';
import { ReduxFormSelect } from '../../FormElements/BlockSelectField';
import { RadioGroupField } from '../../RadioGroupField';
import { GuestFieldProps } from './GuestField.types';
import RemoveGuestButton from './RemoveGuestButton';

import {
  AddButtonWrapper,
  ForenameColumn,
  GenderColumn,
  GuestCounter,
  GuestRow,
  InputsRow,
  OptionalIndicator,
  RadioWrapper,
  RemoveGuestButtonWrapper,
  SurnameColumn,
  TitleColumn,
} from './GuestField.styles';

export const MultiRoomGuestField: FC<GuestFieldProps> = ({
  fields,
  maximumGuests,
  isGenderSpecific = false,
  minimumGuests,
  fieldChange,
  withLeadGuest = false,
  withGenderSelect = true,
  defaultGender,
  showGenderNeutral = false,
  room,
}) => {
  const { t } = useTranslations();

  useEffect(() => {
    // Basic validation - we need the fields array and room data
    if (!fields || !room?.roomNumber || !room?.adults) {
      return;
    }

    const allFields = fields.getAll() || [];

    // Get how many guests this room should have based on room.adults property.
    // Using Math.max ensures we never have negative numbers
    const requiredGuests = Math.max(0, room.adults);

    // Count how many form fields already exist for this specific room.
    // This looks at all fields (empty or filled) where room number matches
    const totalRoomSlots = allFields.filter((guest) => guest.room === room.roomNumber).length;

    // If totalRoomSlots is less than requiredGuests, we need to add more form fields.
    // For example: if room needs 2 guests but only has 1 slot, needsNewSlots will be true
    const needsNewSlots = totalRoomSlots < requiredGuests;

    // Only add new slots if we need them
    if (needsNewSlots) {
      // Calculate exactly how many new slots we need
      // For example: if we need 2 guests but have 1 slot, exactSlotsToAdd will be 1
      const exactSlotsToAdd = requiredGuests - totalRoomSlots;

      // Create an array of empty guest objects, one for each slot we need to add.
      // Each guest object is initialized with empty values but tagged with the correct room number
      const newGuests = Array(exactSlotsToAdd)
        .fill(null)
        .map(() => ({
          room: room.roomNumber,
          title: '',
          firstName: '',
          lastName: '',
          gender: defaultGender || '',
        }));

      // Add each new guest object to the fields array
      newGuests.forEach((guest) => fields.push(guest));
    }
  }, [fields, room?.roomNumber, room?.adults, defaultGender]);

  // Filter the fields array to only show guests for the current room.
  // This is necessary because the fields array contains guests for all rooms
  const roomGuests = fields.getAll()?.filter((guest) => guest.room === room.roomNumber) || [];

  const handleGuestIndexRemove = (index) => {
    fields.remove(index);
    if (fields.length <= minimumGuests) {
      fields.push({
        firstName: '',
        lastName: '',
        gender: defaultGender,
        title: '',
        room: room.roomNumber,
      });
    }
  };

  const handleAddRow = () => {
    const numberOfRowsToAdd =
      fields.length % minimumGuests === 0
        ? minimumGuests
        : minimumGuests - (fields.length % minimumGuests);

    if (!maximumGuests || (maximumGuests && fields.length + numberOfRowsToAdd <= maximumGuests)) {
      new Array(numberOfRowsToAdd).fill(null).forEach(() => {
        fields.push({
          title: '',
          gender: defaultGender,
          value: idGenerator(),
        });
      });
    }
  };

  const titlesOptions = [
    ...(isGenderSpecific === 'M' || isGenderSpecific !== 'F' ? maleTitlesOptions : []),
    ...(isGenderSpecific === 'F' || isGenderSpecific !== 'M' ? femaleTitlesOptions : []),
  ].map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const canAddNextGuest = !maximumGuests || (maximumGuests && roomGuests.length < maximumGuests);

  return (
    <>
      {roomGuests.map((guest, roomIndex) => {
        const absoluteIndex = fields.getAll().findIndex((g) => g === guest);
        const isExistingBasketGuest = !!guest.externalSpaId || !!guest.id;
        const isGenderDataMissing = !(!!guest.title || !!guest.gender);
        const isOptionalIndicatorVisible = roomIndex > 0 && withLeadGuest;
        const guestRoom = roomGuests.find((g) => g.room === room.roomNumber);
        const isFirstGuestInRoom = roomIndex === 0 || guest.room !== roomGuests[roomIndex - 1].room;

        return (
          <GuestRow key={absoluteIndex}>
            {isFirstGuestInRoom ? `${t('common.room')} ${guestRoom.room}` : ''}
            <GuestCounter>
              {withLeadGuest && roomIndex === 0
                ? t('common.leadGuest')
                : t('spa.guestCounter', { counter: roomIndex + 1 })}
              {` `}
              {isOptionalIndicatorVisible && (
                <OptionalIndicator>{t('common.optional')}</OptionalIndicator>
              )}
            </GuestCounter>
            <InputsRow>
              <TitleColumn>
                <Field
                  isFullWidth
                  useNativeDropdownOnMobile={false}
                  component={ReduxFormSelect}
                  labelWidth="50px"
                  name={`guests[${absoluteIndex}].title`}
                  options={titlesOptions}
                  label={t('common.title')}
                  handleAfterChange={(value) => {
                    fieldChange &&
                      fieldChange(
                        `guests[${absoluteIndex}].gender`,
                        value.value === 'Mr' ? 'male' : 'female'
                      );
                  }}
                  isRequired={isFirstGuestInRoom}
                  parse={(selected) => selected && selected.value}
                  format={(selected) => titlesOptions.find(({ value }) => value === selected)}
                  isDisabled={isExistingBasketGuest && !isGenderDataMissing}
                  isSearchable={false}
                  id={`${absoluteIndex}-title`}
                />
              </TitleColumn>
              <ForenameColumn>
                <Field
                  isFullWidth
                  component={ReduxFormInput}
                  isRequired={isFirstGuestInRoom}
                  name={`guests[${absoluteIndex}].firstName`}
                  label={t('common.firstName')}
                  placeholder={t('common.firstName')}
                  disabled={isExistingBasketGuest}
                  id={`${absoluteIndex}-fname`}
                />
              </ForenameColumn>
              <SurnameColumn>
                <Field
                  isFullWidth
                  component={ReduxFormInput}
                  isRequired={isFirstGuestInRoom}
                  name={`guests[${absoluteIndex}].lastName`}
                  label={t('common.lastName')}
                  placeholder={t('common.lastName')}
                  disabled={isExistingBasketGuest}
                  id={`${absoluteIndex}-lname`}
                />
              </SurnameColumn>
              {withGenderSelect && (
                <GenderColumn>
                  <RadioWrapper>
                    <Field
                      name={`guests[${absoluteIndex}].gender`}
                      component={RadioGroupField}
                      cols={3}
                      options={showGenderNeutral ? genderOptionsWithNeutral : genderOptions}
                      isDisabled={!showGenderNeutral && isExistingBasketGuest}
                    />
                  </RadioWrapper>
                </GenderColumn>
              )}
              <RemoveGuestButtonWrapper>
                <RemoveGuestButton
                  isVisible={isExistingBasketGuest || roomIndex >= minimumGuests}
                  onClick={() => handleGuestIndexRemove(absoluteIndex)}
                />
              </RemoveGuestButtonWrapper>
            </InputsRow>
          </GuestRow>
        );
      })}
      <AddButtonWrapper>
        {canAddNextGuest && (
          <Button
            type="button"
            isSmall
            onClick={handleAddRow}
            icon={<AddIcon width="16" height="16" />}
          >
            {t('common.addMoreGuests')}
          </Button>
        )}
      </AddButtonWrapper>
    </>
  );
};
